// Considered: ContextSelectorPopoverMolecule — rejected because it hardcodes
// center_armillary_context as the default system_name and has no Save/dirty-state
// affordances. See spec Reuse Audit in specs/treemap-context-switcher.md.
//
// The switcher popover is context-first (Context Selector design, EAINT redesign):
// each context shows its walk shape (reusing ContextSummaryMolecule compact — the
// canonical walk renderer also used by the Context Library) and, when it has saved
// views, expands to let the user pick a specific view. Picking the context loads the
// visualization's default rendering; picking a view loads that saved rendering.

import React, { useState, useCallback, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { Context } from 'types/context';
import { getContextViewsByContext, type ContextViewRecord } from '../../../api/contextApi';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAvailableContexts } from '../../../hooks/useAvailableContexts';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import DirtyIndicatorAtom from '../../atoms/DirtyIndicator/DirtyIndicatorAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import CustomDialogAtom from '../../atoms/CustomDialog/CustomDialogAtom';
import WalkDiagramAtom from '../../atoms/WalkDiagram/WalkDiagramAtom';
import './ContextSwitcherToolbarMolecule.scss';

export type DirtyKind = 'filters' | 'viewConfig' | 'baseConfig' | 'viewFilters';

export type SwitcherViewType = 'atlas' | 'treemap' | 'kanban' | 'table' | 'timeline' | 'custom';

function viewTypeToIcon(vt: SwitcherViewType): string {
  switch (vt) {
    case 'treemap': return 'pi-th-large';
    case 'kanban': return 'pi-table';
    case 'atlas': return 'pi-globe';
    case 'table': return 'pi-list';
    case 'timeline': return 'pi-calendar';
    case 'custom': return 'pi-apps';
    default: return 'pi-th-large';
  }
}

export interface ContextSwitcherToolbarHandle {
  /** Open the context switcher popover at the given mouse event position */
  open: (event: MouseEvent) => void;
}

export interface ContextSwitcherToolbarMoleculeProps {
  /** Currently active context system name */
  activeContextSystemName: string;
  /** Display name for the active context (already localized) */
  activeContextDisplayName: string;
  /** System name of the component default context (sorted first, labeled "Default") */
  defaultContextSystemName: string;
  /** System name of the active saved view, or null when rendering the context default */
  activeViewSystemName?: string | null;
  /** Display name for the active saved view (already localized) */
  activeViewDisplayName?: string;
  /** Which visualization this switcher serves — gates saved views to that view_type */
  viewType?: SwitcherViewType;
  /** Sphere whose AVAILABLE contexts are offered (deployed-approach providedContexts + built-ins, applicability-gated server-side) */
  sphereId: string | null;
  /** Viewer org; when supplied, a context owned by another org is badged as System */
  organizationId?: string;
  /** Root sphere display label shown in each walk-shape diagram */
  rootLabel?: string;
  /** Whether the user has edit permission on the active context */
  canEditActiveContext: boolean;
  /** True when there are unsaved filter or view changes */
  isDirty: boolean;
  /** What kinds of changes are dirty — drives dirty indicator tooltip text */
  dirtyKinds: DirtyKind[];
  /**
   * Save in place — should be disabled when !canEditActiveContext or !isDirty.
   * Omitted by read-only hosts (those that never go dirty); the save affordance
   * is withheld when absent.
   */
  onSave?: () => Promise<void>;
  /**
   * Save current state as a new context (+ new view if applicable).
   * Omitted by read-only hosts; the save-as-copy affordance is withheld when absent.
   */
  onSaveAsCopy?: (input: { name: string; description?: string }) => Promise<void>;
  /** Discard pending changes. Omitted by read-only hosts, which have none to discard. */
  onResetChanges?: () => void;
  /**
   * Switch to a different context (loads its default rendering).
   *
   * **INVARIANT — implementations MUST clear the active view selection.**
   * A view belongs to exactly one context; carrying it across a context switch
   * makes the view segment show a name that the new context does not own.
   * Reset `activeViewSystemName` to `null` before (or as part of) calling
   * `setActiveContextSystemName`. See EAINT-837.
   */
  onContextSelect: (systemName: string, displayName: string) => void;
  /** Switch to a saved view of a context (loads that saved rendering) */
  onViewSelect?: (
    viewSystemName: string,
    viewDisplayName: string,
    contextSystemName: string,
    contextDisplayName: string
  ) => void;
  /** Optional callback to navigate to the context management page */
  onManageContexts?: () => void;
  /** Optional callback to start creating a brand-new context */
  onNewContext?: () => void;
  /** Optional callback to create a new view from a context */
  onNewView?: (contextSystemName: string, contextDisplayName: string) => void;
  /** Optional callback to edit an existing saved view */
  onEditView?: (viewSystemName: string, contextSystemName: string) => void;
  /** Optional callback to open the editor for an existing context */
  onEditContext?: (contextSystemName: string) => void;
  /** Optional callback fired when the filter-settings (sliders) icon button is clicked */
  onFilterSettings?: () => void;
  /**
   * Increment to force the switcher's view cache to clear and re-fetch after a
   * create / update / delete from the view-editor overlay.
   */
  switcherRefreshKey?: number;
  /** When true, hide the toolbar chip/buttons and only render the popover panels */
  hideChip?: boolean;
}

interface ContextListItem {
  systemName: string;
  displayName: string;
  isDefault: boolean;
  /** Owned by a different org than the viewer ⇒ platform/system context */
  isSystem: boolean;
  /** Computed count of saved views; drives whether the row is expandable */
  viewCount: number;
  context: Context;
}

// ── A context row: walk shape + optional expandable saved views ───────────────
const ContextGroupRow: React.FC<{
  item: ContextListItem;
  rootLabel: string;
  viewType: SwitcherViewType;
  isActiveContext: boolean;
  activeViewSystemName?: string | null;
  expanded: boolean;
  views: ContextViewRecord[] | undefined;
  viewsLoading: boolean;
  onToggle: () => void;
  onPickContext: (item: ContextListItem) => void;
  onPickView: (view: ContextViewRecord, item: ContextListItem) => void;
  onNewView?: (contextSystemName: string, contextDisplayName: string) => void;
  onEditView?: (viewSystemName: string, contextSystemName: string) => void;
  onEditContext?: (contextSystemName: string) => void;
}> = ({
  item,
  rootLabel,
  viewType,
  isActiveContext,
  activeViewSystemName,
  expanded,
  views,
  viewsLoading,
  onToggle,
  onPickContext,
  onPickView,
  onNewView,
  onEditView,
  onEditContext,
}) => {
  const { t } = useTranslation();
  // Expandable only when there are saved views OF THIS view type (the loaded,
  // type-scoped list) — not merely viewCount, which counts all view types. A
  // context with no views of this type loads with the default rendering and
  // never expands to show a lone "Default rendering" row.
  const hasViews = (views?.length ?? 0) > 0;
  // The context default (no view) is "active" when this is the active context
  // and no view is currently selected.
  const contextDefaultActive = isActiveContext && !activeViewSystemName;

  return (
    <div
      className={[
        'context-switcher-toolbar__group',
        isActiveContext ? 'context-switcher-toolbar__group--current' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Header row — selects this context (default rendering) */}
      <button
        type="button"
        className="context-switcher-toolbar__group-header"
        onClick={() => onPickContext(item)}
        data-testid={`ctx-group-${item.systemName}`}
      >
        <span className="context-switcher-toolbar__group-icon">
          <i className={`pi ${item.isSystem ? 'pi-lock' : 'pi-sitemap'}`} aria-hidden="true" />
        </span>
        <span className="context-switcher-toolbar__group-body">
          <span className="context-switcher-toolbar__group-title-row">
            <span className="context-switcher-toolbar__group-name">{item.displayName}</span>
            {item.isSystem && (
              <span className="context-switcher-toolbar__badge">{t('contextSwitcher.systemBadge')}</span>
            )}
            {item.isDefault && (
              <span className="context-switcher-toolbar__hint-italic">
                {t('contextSwitcher.defaultBadge')}
              </span>
            )}
            <span className="context-switcher-toolbar__spacer" />
            {onEditContext && (
              <span
                role="button"
                tabIndex={0}
                className="context-switcher-toolbar__group-edit"
                title={t('contextSwitcher.editContextTooltip')}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditContext(item.systemName);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onEditContext(item.systemName);
                  }
                }}
              >
                <i className="pi pi-pencil" aria-hidden="true" />
              </span>
            )}
            {onNewView && (
              <span
                role="button"
                tabIndex={0}
                className="context-switcher-toolbar__inline-action"
                title={t('contextSwitcher.newViewTooltip')}
                onClick={(e) => {
                  e.stopPropagation();
                  onNewView(item.systemName, item.displayName);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onNewView(item.systemName, item.displayName);
                  }
                }}
              >
                <i className="pi pi-plus" aria-hidden="true" /> {t('contextSwitcher.viewWord')}
              </span>
            )}
          </span>
          <WalkDiagramAtom
            levels={item.context.definition?.levels ?? []}
            rootLabel={rootLabel}
            compact
            className="context-switcher-toolbar__walk"
          />
        </span>
        {hasViews && (
          <span
            role="button"
            tabIndex={0}
            className="context-switcher-toolbar__chevron-toggle"
            aria-label={expanded ? t('contextSwitcher.collapseViews') : t('contextSwitcher.expandViews')}
            aria-expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onToggle();
              }
            }}
          >
            <i className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true" />
          </span>
        )}
      </button>

      {/* Rendering options — only when the context has saved views and is expanded */}
      {hasViews && expanded && (
        <div className="context-switcher-toolbar__views">
          {/* Default rendering */}
          <button
            type="button"
            className={[
              'context-switcher-toolbar__view-row',
              contextDefaultActive ? 'context-switcher-toolbar__view-row--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onPickContext(item)}
            data-testid={`ctx-default-${item.systemName}`}
          >
            <i className="pi pi-circle context-switcher-toolbar__view-icon" aria-hidden="true" />
            <span className="context-switcher-toolbar__view-name context-switcher-toolbar__view-name--muted">
              {t('contextSwitcher.defaultRenderingRow')}
            </span>
            <span className="context-switcher-toolbar__hint-italic">
              {t('contextSwitcher.viewTypeDefaults', { viewType: t(`contextSwitcher.viewType.${viewType}`) })}
            </span>
          </button>

          {viewsLoading && (
            <div className="context-switcher-toolbar__views-loading">
              <i className="pi pi-spin pi-spinner" />
              <span>{t('contextSwitcher.loadingViews')}</span>
            </div>
          )}

          {(views ?? []).map((v) => {
            const isActiveView = v.system_name === activeViewSystemName;
            const viewName = getLocalizedText(v.name, v.system_name);
            return (
              <button
                key={v.system_name}
                type="button"
                className={[
                  'context-switcher-toolbar__view-row',
                  isActiveView ? 'context-switcher-toolbar__view-row--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onPickView(v, item)}
                data-testid={`ctx-view-${v.system_name}`}
              >
                <i className={`pi ${viewTypeToIcon(viewType)} context-switcher-toolbar__view-icon context-switcher-toolbar__view-icon--view`} aria-hidden="true" />
                <span className="context-switcher-toolbar__view-name">{viewName}</span>
                {onEditView && (
                  <span
                    role="button"
                    tabIndex={0}
                    className="context-switcher-toolbar__view-edit"
                    title={t('contextSwitcher.editViewTooltip')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditView(v.system_name, item.systemName);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onEditView(v.system_name, item.systemName);
                      }
                    }}
                  >
                    <i className="pi pi-sliders-h" aria-hidden="true" />
                  </span>
                )}
                {isActiveView ? (
                  <span className="context-switcher-toolbar__view-active-badge">
                    <span className="context-switcher-toolbar__live-dot" />
                    {t('contextSwitcher.activeBadge')}
                  </span>
                ) : (
                  <i className="pi pi-chevron-right context-switcher-toolbar__view-chevron" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ContextSwitcherContent: React.FC<{
  sphereId: string | null;
  /** Viewer org; when supplied, a context owned by another org is badged as System */
  organizationId?: string;
  activeContextSystemName: string;
  activeViewSystemName?: string | null;
  defaultContextSystemName: string;
  viewType: SwitcherViewType;
  rootLabel: string;
  onContextSelect: (systemName: string, displayName: string) => void;
  onViewSelect?: (
    viewSystemName: string,
    viewDisplayName: string,
    contextSystemName: string,
    contextDisplayName: string
  ) => void;
  onClose: () => void;
  onManageContexts?: () => void;
  onNewContext?: () => void;
  onNewView?: (contextSystemName: string, contextDisplayName: string) => void;
  onEditView?: (viewSystemName: string, contextSystemName: string) => void;
  onEditContext?: (contextSystemName: string) => void;
  switcherRefreshKey?: number;
}> = ({
  sphereId,
  organizationId,
  activeContextSystemName,
  activeViewSystemName,
  defaultContextSystemName,
  viewType,
  rootLabel,
  onContextSelect,
  onViewSelect,
  onClose,
  onManageContexts,
  onNewContext,
  onNewView,
  onEditView,
  onEditContext,
  switcherRefreshKey,
}) => {
  const { t, i18n } = useTranslation();
  const apiClients = useApiClientsOrNull();
  // Available contexts = provided by approaches deployed on the sphere or its
  // ancestors + built-ins, applicability evaluated server-side. Keeps the
  // offering scoped to where each context's providing approach is deployed.
  const { contexts: availableContexts, loading, error: fetchError } = useAvailableContexts(
    apiClients,
    sphereId
  );

  const [query, setQuery] = useState('');
  const [expandedSystemName, setExpandedSystemName] = useState<string | null>(null);
  // Lazily-loaded views keyed by context system_name (filtered to this view_type).
  const [viewsByContext, setViewsByContext] = useState<Record<string, ContextViewRecord[]>>({});
  const [viewsLoading, setViewsLoading] = useState<Record<string, boolean>>({});
  const [focusedIndex, setFocusedIndex] = useState(0);
  const groupsRef = useRef<HTMLDivElement>(null);

  // Clear the view cache when the parent signals a post-save/delete refresh.
  // The cleared entries will be re-fetched lazily on the next expand.
  useEffect(() => {
    setViewsByContext({});
  }, [switcherRefreshKey]);

  const getLocalizedName = useCallback(
    (context: Context): string => {
      const lang = i18n.language;
      if (context.name.translations?.[lang]) {
        return context.name.translations[lang];
      }
      return context.name.default;
    },
    [i18n.language]
  );

  const contexts = useMemo((): ContextListItem[] => {
    const items: ContextListItem[] = availableContexts.map((c) => ({
      systemName: c.system_name,
      // Always show the real localized name — the design surfaces each context by
      // its own name plus a "default rendering" hint.
      displayName: getLocalizedName(c),
      isDefault: c.system_name === defaultContextSystemName,
      // Without a viewer org we can't classify ownership; don't claim System.
      isSystem: organizationId ? c.organization_id !== organizationId : false,
      viewCount: c.viewCount ?? 0,
      context: c,
    }));

    items.sort((a, b) => {
      // Active context floats to the top, then default, then alphabetical.
      if (a.systemName === activeContextSystemName) return -1;
      if (b.systemName === activeContextSystemName) return 1;
      if (a.isDefault) return -1;
      if (b.isDefault) return 1;
      return a.displayName.localeCompare(b.displayName);
    });

    return items;
  }, [availableContexts, organizationId, defaultContextSystemName, activeContextSystemName, t, getLocalizedName]);

  const error = fetchError ? t('contextSwitcher.errorLoadingContexts') : null;

  // Lazily fetch a context's saved views the first time it expands.
  const loadViews = useCallback(
    (systemName: string) => {
      if (!apiClients?.schemaClient) return;
      if (viewsByContext[systemName] || viewsLoading[systemName]) return;
      setViewsLoading((prev) => ({ ...prev, [systemName]: true }));
      getContextViewsByContext(apiClients.schemaClient, systemName)
        .then((records) => {
          const scoped = (Array.isArray(records) ? records : []).filter(
            (r) => r.view_type === viewType
          );
          setViewsByContext((prev) => ({ ...prev, [systemName]: scoped }));
        })
        .catch(() => {
          setViewsByContext((prev) => ({ ...prev, [systemName]: [] }));
        })
        .finally(() => {
          setViewsLoading((prev) => ({ ...prev, [systemName]: false }));
        });
    },
    [apiClients, viewsByContext, viewsLoading, viewType]
  );

  // Eager-load views for every context that reports having some, so each row's
  // chevron accurately reflects whether there are saved views OF THIS view type
  // (viewCount is all-types; only the type-scoped fetch tells us the truth).
  useEffect(() => {
    contexts.forEach((c) => {
      if (c.viewCount > 0) loadViews(c.systemName);
    });
  }, [contexts, loadViews]);

  // Auto-expand the active context once its type-scoped views are known to be
  // non-empty (so we never expand it to a lone "Default rendering" row).
  useEffect(() => {
    const scoped = viewsByContext[activeContextSystemName];
    if (scoped && scoped.length > 0) {
      setExpandedSystemName(activeContextSystemName);
    }
  }, [activeContextSystemName, viewsByContext]);

  const handleToggle = useCallback(
    (systemName: string) => {
      setExpandedSystemName((prev) => {
        const next = prev === systemName ? null : systemName;
        if (next) loadViews(next);
        return next;
      });
    },
    [loadViews]
  );

  const handlePickContext = useCallback(
    (item: ContextListItem) => {
      onContextSelect(item.systemName, item.displayName);
      onClose();
    },
    [onContextSelect, onClose]
  );

  const handlePickView = useCallback(
    (view: ContextViewRecord, item: ContextListItem) => {
      if (!onViewSelect) return;
      onViewSelect(
        view.system_name,
        getLocalizedText(view.name, view.system_name),
        item.systemName,
        item.displayName
      );
      onClose();
    },
    [onViewSelect, onClose]
  );

  const q = query.trim().toLowerCase();
  const filtered = q
    ? contexts.filter(
        (c) =>
          c.displayName.toLowerCase().includes(q) ||
          (viewsByContext[c.systemName] ?? []).some((v) =>
            getLocalizedText(v.name, v.system_name).toLowerCase().includes(q)
          )
      )
    : contexts;

  // Keyboard navigation for arrow keys and Enter
  const handleGroupsKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const buttons = groupsRef.current?.querySelectorAll('button[type="button"]:not(:disabled)') as NodeListOf<HTMLButtonElement> | undefined;
        if (!buttons || buttons.length === 0) return;

        const nextIndex = e.key === 'ArrowDown'
          ? Math.min(focusedIndex + 1, buttons.length - 1)
          : Math.max(focusedIndex - 1, 0);

        setFocusedIndex(nextIndex);
        buttons[nextIndex]?.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const focusedButton = groupsRef.current?.querySelector('button:focus') as HTMLButtonElement | null;
        focusedButton?.click();
      }
    },
    [focusedIndex]
  );

  // Auto-focus first button when popover opens
  useEffect(() => {
    const buttons = groupsRef.current?.querySelectorAll('button[type="button"]:not(:disabled)') as NodeListOf<HTMLButtonElement> | undefined;
    if (buttons && buttons.length > 0) {
      setFocusedIndex(0);
      buttons[0]?.focus();
    }
  }, [filtered]);

  return (
    <div className="context-switcher-toolbar__popover-inner">
      {/* Search */}
      <div className="context-switcher-toolbar__search">
        <i className="pi pi-search context-switcher-toolbar__search-icon" aria-hidden="true" />
        <input
          autoFocus
          type="text"
          className="context-switcher-toolbar__search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('contextSwitcher.searchPlaceholder')}
          aria-label={t('contextSwitcher.searchPlaceholder')}
        />
        <span className="context-switcher-toolbar__view-type-badge">
          {viewType.toUpperCase()}
        </span>
      </div>

      {/* Section label */}
      <div className="context-switcher-toolbar__section-label">
        <i className="pi pi-clone context-switcher-toolbar__section-icon" aria-hidden="true" />
        {t('contextSwitcher.contextsSection')}
        <span className="context-switcher-toolbar__spacer" />
        <span className="context-switcher-toolbar__section-hint">{t('contextSwitcher.expandHint')}</span>
      </div>

      {/* List */}
      <div
        className="context-switcher-toolbar__groups"
        ref={groupsRef}
        onKeyDown={handleGroupsKeyDown}
        role="listbox"
        tabIndex={-1}
      >
        {loading && (
          <div className="context-switcher-toolbar__list-loading">
            <i className="pi pi-spin pi-spinner" />
            <span>{t('contextSwitcher.loadingContexts')}</span>
          </div>
        )}
        {error && !loading && (
          <div className="context-switcher-toolbar__list-error">
            <i className="pi pi-exclamation-triangle" />
            <span>{error}</span>
          </div>
        )}
        {!loading &&
          !error &&
          filtered.map((item) => (
            <ContextGroupRow
              key={item.systemName}
              item={item}
              rootLabel={rootLabel}
              viewType={viewType}
              isActiveContext={item.systemName === activeContextSystemName}
              activeViewSystemName={activeViewSystemName}
              expanded={expandedSystemName === item.systemName}
              views={viewsByContext[item.systemName]}
              viewsLoading={!!viewsLoading[item.systemName]}
              onToggle={() => handleToggle(item.systemName)}
              onPickContext={handlePickContext}
              onPickView={handlePickView}
              onNewView={onNewView}
              onEditView={onEditView}
              onEditContext={onEditContext}
            />
          ))}
        {!loading && !error && filtered.length === 0 && (
          <div className="context-switcher-toolbar__list-empty">
            {q
              ? t('contextSwitcher.noMatches', { query })
              : t('contextSwitcher.noContextsAvailable')}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="context-switcher-toolbar__footer">
        <span className="context-switcher-toolbar__kbd-hints">
          <kbd className="context-switcher-toolbar__kbd">↑</kbd>
          <kbd className="context-switcher-toolbar__kbd">↓</kbd>
          <span className="context-switcher-toolbar__kbd-label">{t('contextSwitcher.navigate')}</span>
          <kbd className="context-switcher-toolbar__kbd">↵</kbd>
          <span className="context-switcher-toolbar__kbd-label">{t('contextSwitcher.open')}</span>
        </span>
        <span className="context-switcher-toolbar__spacer" />
        {onNewContext && (
          <button
            type="button"
            className="context-switcher-toolbar__footer-action context-switcher-toolbar__footer-action--primary"
            onClick={() => {
              onClose();
              onNewContext();
            }}
          >
            <i className="pi pi-plus" aria-hidden="true" /> {t('contextSwitcher.newContext')}
          </button>
        )}
        {onManageContexts && (
          <button
            type="button"
            className="context-switcher-toolbar__footer-action"
            onClick={() => {
              onClose();
              onManageContexts();
            }}
          >
            {t('contextSwitcher.manageContexts')} <i className="pi pi-external-link" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

// ── View-segment lightweight popover ─────────────────────────────────────────
// Opened by clicking the VIEW segment of the split chip. Shows only the views
// for the currently active context; has no search or context list.
const ViewSwitcherContent: React.FC<{
  activeContextSystemName: string;
  activeContextDisplayName: string;
  viewType: SwitcherViewType;
  activeViewSystemName?: string | null;
  switcherRefreshKey?: number;
  onPickDefault: () => void;
  onPickView: (viewSystemName: string, viewDisplayName: string, contextSystemName: string, contextDisplayName: string) => void;
  onNewView?: () => void;
  onSwitchContext: () => void;
  /** Open the view editor for a non-active view without switching to it first. */
  onEditView?: (viewSystemName: string, contextSystemName: string) => void;
  onClose: () => void;
}> = ({
  activeContextSystemName,
  activeContextDisplayName,
  viewType,
  activeViewSystemName,
  switcherRefreshKey,
  onPickDefault,
  onPickView,
  onNewView,
  onSwitchContext,
  onEditView,
  onClose,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const [views, setViews] = useState<ContextViewRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!apiClients?.schemaClient) return;
    setLoading(true);
    getContextViewsByContext(apiClients.schemaClient, activeContextSystemName)
      .then((records) => {
        const scoped = (Array.isArray(records) ? records : []).filter(
          (r) => r.view_type === viewType
        );
        setViews(scoped);
      })
      .catch(() => setViews([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClients, activeContextSystemName, viewType, switcherRefreshKey]);

  const contextDefaultActive = !activeViewSystemName;
  const viewIcon = viewTypeToIcon(viewType);

  return (
    <div className="context-switcher-toolbar__view-panel">
      <div className="context-switcher-toolbar__view-panel-header">
        <i className={`pi ${viewIcon} context-switcher-toolbar__view-panel-header-icon`} aria-hidden="true" />
        <span>
          {t('contextSwitcher.viewsOf', { contextName: activeContextDisplayName })}
        </span>
      </div>

      <div className="context-switcher-toolbar__view-panel-list">
        {/* Default (no saved view) */}
        <button
          type="button"
          className={[
            'context-switcher-toolbar__view-panel-row',
            contextDefaultActive ? 'context-switcher-toolbar__view-panel-row--active' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => { onPickDefault(); onClose(); }}
        >
          <i className="pi pi-circle context-switcher-toolbar__view-panel-icon" aria-hidden="true" />
          <span className="context-switcher-toolbar__view-panel-name context-switcher-toolbar__view-panel-name--muted">
            {t('contextSwitcher.defaultView')}
          </span>
          <span className="context-switcher-toolbar__hint-italic">
            {t('contextSwitcher.viewTypeDefaults', { viewType: t(`contextSwitcher.viewType.${viewType}`) })}
          </span>
        </button>

        {loading && (
          <div className="context-switcher-toolbar__views-loading">
            <i className="pi pi-spin pi-spinner" />
          </div>
        )}

        {!loading && views.length === 0 && (
          <p className="context-switcher-toolbar__view-panel-empty">
            {t('contextSwitcher.noViews')}
          </p>
        )}

        {views.map((v) => {
          const isActive = v.system_name === activeViewSystemName;
          const viewName = getLocalizedText(v.name, v.system_name);
          return (
            // The row and its settings affordance are SIBLINGS, not nested. They used to
            // be a `<span role="button">` inside the row `<button>` — interactive content
            // inside a button is invalid HTML, so the span could not simply become a
            // button, and it had to hand-roll tabIndex + Enter/Space to work at all.
            // Splitting them lets both be real buttons; the wrapper carries the row
            // layout the outer button used to own.
            <div
              key={v.system_name}
              className={[
                'context-switcher-toolbar__view-panel-row-wrap',
                isActive ? 'context-switcher-toolbar__view-panel-row-wrap--active' : '',
              ].filter(Boolean).join(' ')}
            >
            <button
              type="button"
              className={[
                'context-switcher-toolbar__view-panel-row',
                isActive ? 'context-switcher-toolbar__view-panel-row--active' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => {
                onPickView(v.system_name, viewName, activeContextSystemName, activeContextDisplayName);
                onClose();
              }}
            >
              <i className={`pi ${viewIcon} context-switcher-toolbar__view-panel-icon context-switcher-toolbar__view-panel-icon--view`} aria-hidden="true" />
              <span className="context-switcher-toolbar__view-panel-name">{viewName}</span>
              {isActive && (
                <span className="context-switcher-toolbar__view-active-badge">
                  <span className="context-switcher-toolbar__live-dot" />
                  {t('contextSwitcher.activeBadge')}
                </span>
              )}
            </button>
              <button
                type="button"
                className="context-switcher-toolbar__view-panel-settings-btn"
                aria-label={t('contextSwitcher.editViewTooltip')}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditView?.(v.system_name, activeContextSystemName);
                  onClose();
                }}
              >
                <i className="pi pi-pencil" aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="context-switcher-toolbar__view-panel-footer">
        {onNewView && (
          <button
            type="button"
            className="context-switcher-toolbar__view-panel-footer-action context-switcher-toolbar__view-panel-footer-action--primary"
            onClick={() => { onNewView(); onClose(); }}
          >
            <i className="pi pi-plus" aria-hidden="true" />
            {t('contextSwitcher.newViewFromContext')}
          </button>
        )}
        <button
          type="button"
          className="context-switcher-toolbar__view-panel-footer-action"
          onClick={() => onSwitchContext()}
        >
          {t('contextSwitcher.switchContext')}
          <span className="context-switcher-toolbar__view-panel-footer-hint">
            {t('contextSwitcher.allContexts')}
          </span>
        </button>
      </div>
    </div>
  );
};

const ContextSwitcherToolbarMolecule = React.forwardRef<
  ContextSwitcherToolbarHandle,
  ContextSwitcherToolbarMoleculeProps
>(({
  activeContextSystemName,
  activeContextDisplayName,
  defaultContextSystemName,
  activeViewSystemName = null,
  activeViewDisplayName,
  viewType = 'treemap',
  rootLabel,
  sphereId,
  organizationId,
  canEditActiveContext,
  isDirty,
  dirtyKinds,
  onSave,
  onSaveAsCopy,
  onResetChanges,
  onContextSelect,
  onViewSelect,
  onManageContexts,
  onNewContext,
  onNewView,
  onEditView,
  onEditContext,
  onFilterSettings,
  switcherRefreshKey,
  hideChip = false,
}, ref) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const overlayRef = useRef<OverlayPanel>(null);
  const viewOverlayRef = useRef<OverlayPanel>(null);
  const overflowRef = useRef<OverlayPanel>(null);
  const contextSegmentRef = useRef<HTMLButtonElement>(null);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [isViewPopoverVisible, setIsViewPopoverVisible] = useState(false);
  const [isSaveAsCopyOpen, setIsSaveAsCopyOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  // A pending selection deferred behind the discard-changes prompt. Carries either
  // a context pick or a view pick so the same guard covers both.
  const [pendingSwitch, setPendingSwitch] = useState<
    | { kind: 'context'; systemName: string; displayName: string }
    | {
        kind: 'view';
        viewSystemName: string;
        viewDisplayName: string;
        contextSystemName: string;
        contextDisplayName: string;
      }
    | null
  >(null);
  // Whether the CURRENT pendingSwitch was blocked because of the shared-filter
  // buffer specifically (`dirtyKinds` at the moment the guard raised), not
  // just any dirty rail — `context_filter_switch_blocked` exists to measure
  // the blast-radius rule this spec draws (session/personal never block,
  // shared-view edits do), so it must not fire for a `filters`/`viewConfig`-only
  // guard raise (EAINT-789 § Analytics).
  const [pendingSwitchBlockedByViewFilters, setPendingSwitchBlockedByViewFilters] = useState(false);
  const [saveAsCopyName, setSaveAsCopyName] = useState('');
  const [saveAsCopyDescription, setSaveAsCopyDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Expose imperative handle for external trigger (e.g. filter icon in Armillary)
  useImperativeHandle(ref, () => ({
    open: (event: MouseEvent) => {
      if (!overlayRef.current) return;
      event.stopPropagation();

      const clickX = event.clientX;
      const clickY = event.clientY;
      const popupWidth = 480;
      const popupHeight = 500; // Approximate height of the popup
      const gap = 8;

      overlayRef.current.toggle(event as unknown as React.SyntheticEvent);

      // After PrimeReact renders the panel, override its position using the actual click coordinates
      // (same pattern as ContextSelectorPopoverMolecule and OrbitalVisualizationMolecule)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const panelEl = document.querySelector('.context-switcher-panel') as HTMLElement | null;
          if (!panelEl || panelEl.style.display === 'none') return;

          // Horizontal positioning: prefer right side of click, fall back to left
          const left = (window.innerWidth - clickX) >= popupWidth + gap
            ? clickX
            : Math.max(gap, clickX - popupWidth);

          // Vertical positioning: prefer below click, fall back to above if not enough space
          const spaceBelow = window.innerHeight - clickY;
          const top = spaceBelow >= popupHeight + gap
            ? clickY + gap
            : Math.max(gap, clickY - popupHeight - gap);

          panelEl.style.position = 'fixed';
          panelEl.style.top = `${top}px`;
          panelEl.style.left = `${left}px`;
        });
      });
    },
  }), []);

  // Tracks whether the view editor was opened via the settings segment,
  // driving the blue tint on the icon. Resets when either other popover opens.
  const [isSettingsActive, setIsSettingsActive] = useState(false);

  const handleChipClick = useCallback((e: React.MouseEvent) => {
    setIsSettingsActive(false);
    viewOverlayRef.current?.hide();
    overlayRef.current?.toggle(e);
  }, []);

  const handleViewChipClick = useCallback((e: React.MouseEvent) => {
    setIsSettingsActive(false);
    overlayRef.current?.hide();
    viewOverlayRef.current?.toggle(e);
  }, []);

  // Close the view popover and open the context popover (programmatic, used by
  // "Switch context…" in the view panel footer).
  const handleSwitchContextFromView = useCallback(() => {
    viewOverlayRef.current?.hide();
    setTimeout(() => {
      contextSegmentRef.current?.click();
    }, 80);
  }, []);

  // Opens the view editor for the CURRENT view directly — no list, no menu step.
  // Named view active → edit it. Default rendering → new-view flow, context pre-bound.
  // Icon collision with onFilterSettings resolved here: settings uses pi-cog, filter keeps pi-sliders-h. (EAINT-836)
  const handleSettingsClick = useCallback(() => {
    setIsSettingsActive(true);
    overlayRef.current?.hide();
    viewOverlayRef.current?.hide();
    if (activeViewSystemName) {
      onEditView?.(activeViewSystemName, activeContextSystemName);
    } else {
      onNewView?.(activeContextSystemName, activeContextDisplayName);
    }
  }, [activeViewSystemName, activeContextSystemName, activeContextDisplayName, onEditView, onNewView]);

  const handleEditContext = useCallback(
    (ctxSn: string) => {
      overlayRef.current?.hide();
      onEditContext?.(ctxSn);
    },
    [onEditContext]
  );

  const handleOverflowClick = useCallback((e: React.MouseEvent) => {
    overflowRef.current?.toggle(e);
  }, []);

  // Commit a deferred selection (after discard / save-first), regardless of kind.
  const commitPending = useCallback(
    (pending: NonNullable<typeof pendingSwitch>) => {
      if (pending.kind === 'context') {
        onContextSelect(pending.systemName, pending.displayName);
      } else {
        onViewSelect?.(
          pending.viewSystemName,
          pending.viewDisplayName,
          pending.contextSystemName,
          pending.contextDisplayName
        );
      }
    },
    [onContextSelect, onViewSelect]
  );

  // Fires `context_filter_switch_blocked` for the CURRENT pending switch,
  // only when it was raised (at least in part) by the shared-filter buffer —
  // never for a guard raised purely by the definition rail.
  const logSwitchGuardResolution = useCallback(
    (resolution: 'save' | 'discard' | 'cancel') => {
      if (!pendingSwitchBlockedByViewFilters) return;
      logAnalyticsEvent('context_filter_switch_blocked', undefined, {
        contextSystemName: activeContextSystemName,
        resolution,
      });
    },
    [pendingSwitchBlockedByViewFilters, logAnalyticsEvent, activeContextSystemName]
  );

  const handleContextSelect = useCallback(
    (systemName: string, displayName: string) => {
      if (isDirty) {
        setPendingSwitch({ kind: 'context', systemName, displayName });
        setPendingSwitchBlockedByViewFilters(dirtyKinds.includes('viewFilters'));
        setIsDiscardOpen(true);
      } else {
        onContextSelect(systemName, displayName);
      }
    },
    [isDirty, onContextSelect, dirtyKinds]
  );

  const handleViewSelect = useCallback(
    (
      viewSystemName: string,
      viewDisplayName: string,
      contextSystemName: string,
      contextDisplayName: string
    ) => {
      if (isDirty) {
        setPendingSwitch({
          kind: 'view',
          viewSystemName,
          viewDisplayName,
          contextSystemName,
          contextDisplayName,
        });
        setPendingSwitchBlockedByViewFilters(dirtyKinds.includes('viewFilters'));
        setIsDiscardOpen(true);
      } else {
        onViewSelect?.(viewSystemName, viewDisplayName, contextSystemName, contextDisplayName);
      }
    },
    [isDirty, onViewSelect, dirtyKinds]
  );

  const handleDiscardConfirm = useCallback(() => {
    if (!pendingSwitch) return;
    logSwitchGuardResolution('discard');
    commitPending(pendingSwitch);
    onResetChanges?.();
    setIsDiscardOpen(false);
    setPendingSwitch(null);
    setPendingSwitchBlockedByViewFilters(false);
  }, [pendingSwitch, commitPending, onResetChanges, logSwitchGuardResolution]);

  // Never commit a switch whose save failed — a rejected `onSave` (e.g. the
  // shared-filter write refused with a 403 once permission was revoked
  // mid-session, EAINT-789 Edge Cases) means the pending change is still
  // pending, and committing the switch anyway would silently discard it
  // with no error surfaced to the user.
  const handleSaveFirst = useCallback(async () => {
    if (!pendingSwitch) return;
    logSwitchGuardResolution('save');
    setIsDiscardOpen(false);
    try {
      await onSave?.();
    } catch {
      // The save failed (e.g. a 403 once shared-view edit permission was
      // revoked mid-session) — cancel the switch rather than commit it.
      // The caller's own pending state (the shared-filter buffer) is left
      // untouched by a failed `onSave`, so nothing here is lost, only the
      // navigation the user asked for does not happen.
      setPendingSwitch(null);
      setPendingSwitchBlockedByViewFilters(false);
      return;
    }
    commitPending(pendingSwitch);
    setPendingSwitch(null);
    setPendingSwitchBlockedByViewFilters(false);
  }, [pendingSwitch, onSave, commitPending, logSwitchGuardResolution]);

  const handleDiscardCancel = useCallback(() => {
    logSwitchGuardResolution('cancel');
    setIsDiscardOpen(false);
    setPendingSwitch(null);
    setPendingSwitchBlockedByViewFilters(false);
  }, [logSwitchGuardResolution]);

  const handleSaveAsCopyOpen = useCallback(() => {
    setSaveAsCopyName(`${activeContextDisplayName}${t('contextSwitcher.saveNameSuffix')}`);
    setSaveAsCopyDescription('');
    setIsSaveAsCopyOpen(true);
    overflowRef.current?.hide();
  }, [activeContextDisplayName, t]);

  const handleSaveAsCopySubmit = useCallback(async () => {
    if (!saveAsCopyName.trim()) return;
    setIsSaving(true);
    try {
      await onSaveAsCopy?.({
        name: saveAsCopyName.trim(),
        description: saveAsCopyDescription.trim() || undefined,
      });
      setIsSaveAsCopyOpen(false);
    } finally {
      setIsSaving(false);
    }
  }, [saveAsCopyName, saveAsCopyDescription, onSaveAsCopy]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    overflowRef.current?.hide();
    try {
      await onSave?.();
    } catch {
      // `onSave` has already surfaced the failure (toast); nothing pending
      // here to cancel, so there is nothing further to do with the error.
    } finally {
      setIsSaving(false);
    }
  }, [onSave]);

  // Close the popover before opening the overlay editor, so the switcher
  // doesn't sit behind the overlay when the user is authoring.
  const handleNewView = useCallback(
    (ctxSn: string, ctxName: string) => {
      overlayRef.current?.hide();
      onNewView?.(ctxSn, ctxName);
    },
    [onNewView]
  );

  const handleEditView = useCallback(
    (viewSn: string, ctxSn: string) => {
      overlayRef.current?.hide();
      onEditView?.(viewSn, ctxSn);
    },
    [onEditView]
  );

  const dirtyKindsLabel = dirtyKinds
    .map((k) => {
      if (k === 'filters') return t('contextSwitcher.dirtyFilters');
      if (k === 'viewConfig') return t('contextSwitcher.dirtyViewConfig');
      if (k === 'viewFilters') return t('contextSwitcher.dirtyViewFilters');
      return t('contextSwitcher.dirtyBaseConfig');
    })
    .join(', ');

  const unsavedLabel =
    dirtyKinds.length === 1
      ? t('contextSwitcher.unsavedChange', { count: dirtyKinds.length })
      : t('contextSwitcher.unsavedChanges', { count: dirtyKinds.length });

  let pendingTargetName = '';
  if (pendingSwitch) {
    pendingTargetName =
      pendingSwitch.kind === 'context' ? pendingSwitch.displayName : pendingSwitch.viewDisplayName;
  }

  const discardMessage = pendingSwitch
    ? t('contextSwitcher.discardMessage', {
        count: dirtyKinds.length,
        contextName: activeContextDisplayName,
        targetName: pendingTargetName,
      })
    : '';

  const saveAsCopyFooter = (
    <div className="context-switcher-toolbar__dialog-footer">
      <ButtonAtom
        label={t('contextSwitcher.cancel')}
        variant="secondary"
        size="small"
        onClick={() => setIsSaveAsCopyOpen(false)}
        disabled={isSaving}
      />
      <ButtonAtom
        label={t('contextSwitcher.saveCopy')}
        variant="primary"
        size="small"
        onClick={handleSaveAsCopySubmit}
        disabled={!saveAsCopyName.trim() || isSaving}
        loading={isSaving}
      />
    </div>
  );

  const discardFooter = (
    <div className="context-switcher-toolbar__dialog-footer">
      <ButtonAtom
        label={t('contextSwitcher.cancel')}
        variant="secondary"
        size="small"
        onClick={handleDiscardCancel}
      />
      {canEditActiveContext && (
        <ButtonAtom
          label={t('contextSwitcher.saveFirst')}
          variant="secondary"
          size="small"
          onClick={handleSaveFirst}
        />
      )}
      <ButtonAtom
        label={t('contextSwitcher.discard')}
        variant="primary"
        size="small"
        onClick={handleDiscardConfirm}
      />
    </div>
  );

  return (
    <>
      {!hideChip && (
      <div className="context-switcher-toolbar" data-testid="context-switcher-toolbar">
        {/* Three-segment control: context (quiet) | view (loud) | settings (icon-only)
            Segment weight: view > settings > context — do NOT make equal-weight.
            EAINT-834 makes view always visible; EAINT-835 wires the ViewMenu; EAINT-836 wires settings. */}
        <div className="context-switcher-toolbar__chip-group">
          {/* 1. Context segment — quiet; opens full SwitcherPopover (behaviour unchanged) */}
          <button
            ref={contextSegmentRef}
            type="button"
            className="context-switcher-toolbar__segment context-switcher-toolbar__segment--context"
            onClick={handleChipClick}
            aria-haspopup="listbox"
            aria-expanded={isPopoverVisible}
            data-testid="context-switcher-chip"
          >
            <i className="pi pi-sitemap context-switcher-toolbar__chip-icon context-switcher-toolbar__chip-icon--muted" aria-hidden="true" />
            <span className="context-switcher-toolbar__chip-name context-switcher-toolbar__chip-name--context">{activeContextDisplayName}</span>
            <i className="pi pi-chevron-down context-switcher-toolbar__chip-chevron" aria-hidden="true" />
          </button>

          {/* 2. View segment — always visible (EAINT-834).
              Active view → name + blue VIEW badge.
              Default rendering → "Default view" + grey DEFAULT badge.
              Never shows an empty label. */}
          <button
            type="button"
            className="context-switcher-toolbar__segment context-switcher-toolbar__segment--view"
            onClick={handleViewChipClick}
            aria-haspopup="listbox"
            aria-expanded={isViewPopoverVisible}
            data-testid="context-switcher-view-chip"
          >
            <i className="pi pi-th-large context-switcher-toolbar__chip-icon context-switcher-toolbar__chip-icon--view" aria-hidden="true" />
            <span className={[
              'context-switcher-toolbar__chip-name',
              activeViewDisplayName ? 'context-switcher-toolbar__chip-name--view' : 'context-switcher-toolbar__chip-name--context',
            ].join(' ')}>
              {activeViewDisplayName ?? t('contextSwitcher.defaultView')}
            </span>
            <span className={[
              'context-switcher-toolbar__chip-view-badge',
              !activeViewDisplayName ? 'context-switcher-toolbar__chip-view-badge--default' : '',
            ].filter(Boolean).join(' ')}>
              {activeViewDisplayName ? t('contextSwitcher.viewWord') : t('contextSwitcher.defaultBadge')}
            </span>
            <i className="pi pi-chevron-down context-switcher-toolbar__chip-chevron" aria-hidden="true" />
          </button>

          {/* 3. Settings segment — opens the view editor for the CURRENT view in one click.
              Icon: custom 2-line horizontal sliders SVG (design-spec settings-2 style). EAINT-836. */}
          <button
            type="button"
            className="context-switcher-toolbar__segment context-switcher-toolbar__segment--settings"
            aria-label={t('contextSwitcher.viewSettings')}
            data-testid="view-settings-gear"
            onClick={handleSettingsClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={15}
              height={15}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.9}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={[
                'context-switcher-toolbar__chip-icon',
                isSettingsActive ? 'context-switcher-toolbar__chip-icon--settings-active' : 'context-switcher-toolbar__chip-icon--settings',
              ].join(' ')}
              aria-hidden="true"
            >
              <line x1="3" y1="8" x2="21" y2="8" />
              <circle cx="16" cy="8" r="2.5" fill="currentColor" stroke="none" />
              <line x1="3" y1="16" x2="21" y2="16" />
              <circle cx="8" cy="16" r="2.5" fill="currentColor" stroke="none" />
            </svg>
          </button>
        </div>

        {/* Filter-settings icon — standalone button to the right of the chip group */}
        {onFilterSettings && (
          <button
            type="button"
            className="context-switcher-toolbar__filter-btn"
            onClick={onFilterSettings}
            aria-label={t('contextSwitcher.filterSettings')}
            data-testid="context-switcher-filter-btn"
          >
            <i className="pi pi-sliders-h" aria-hidden="true" />
          </button>
        )}

        {/* Dirty state indicator */}
        {isDirty && (
          <div className="context-switcher-toolbar__dirty" data-testid="dirty-indicator">
            <DirtyIndicatorAtom
              variant="dot"
              show
              title={dirtyKindsLabel}
              className="context-switcher-toolbar__dirty-dot"
            />
            <span className="context-switcher-toolbar__dirty-label" title={dirtyKindsLabel}>
              {unsavedLabel}
            </span>
          </div>
        )}

        {/* Wide viewport save actions */}
        {isDirty && (
          <div className="context-switcher-toolbar__actions" data-testid="save-actions">
            <ButtonAtom
              label={t('contextSwitcher.save')}
              variant="secondary"
              size="small"
              onClick={handleSave}
              disabled={!canEditActiveContext || !onSave || isSaving}
              tooltip={canEditActiveContext ? undefined : t('contextSwitcher.cannotSave')}
              tooltipOptions={{ position: 'bottom' }}
              loading={isSaving}
              data-testid="save-btn"
            />
            <ButtonAtom
              label={t('contextSwitcher.saveAsCopy')}
              variant="secondary"
              size="small"
              onClick={handleSaveAsCopyOpen}
              disabled={!onSaveAsCopy || isSaving}
              data-testid="save-as-copy-btn"
            />
          </div>
        )}

        {/* Narrow viewport overflow button (hidden on wide screens via CSS) */}
        {isDirty && (
          <button
            type="button"
            className="context-switcher-toolbar__overflow-btn"
            onClick={handleOverflowClick}
            aria-label={t('contextSwitcher.moreActions')}
            data-testid="overflow-btn"
          >
            <i className="pi pi-ellipsis-h" aria-hidden="true" />
          </button>
        )}
      </div>
      )}

      {/* Context list popover */}
      <OverlayPanel
        ref={overlayRef}
        className="context-switcher-toolbar__popover context-switcher-panel"
        onShow={() => setIsPopoverVisible(true)}
        onHide={() => setIsPopoverVisible(false)}
        dismissable
        appendTo={document.body}
      >
        {isPopoverVisible && (
          <ContextSwitcherContent
            sphereId={sphereId}
            organizationId={organizationId}
            activeContextSystemName={activeContextSystemName}
            activeViewSystemName={activeViewSystemName}
            defaultContextSystemName={defaultContextSystemName}
            viewType={viewType}
            rootLabel={rootLabel ?? t('contextSwitcher.rootFallback')}
            onContextSelect={handleContextSelect}
            onViewSelect={handleViewSelect}
            onClose={() => overlayRef.current?.hide()}
            onManageContexts={onManageContexts}
            onNewContext={onNewContext}
            onNewView={onNewView ? handleNewView : undefined}
            onEditView={onEditView ? handleEditView : undefined}
            onEditContext={onEditContext ? handleEditContext : undefined}
            switcherRefreshKey={switcherRefreshKey}
          />
        )}
      </OverlayPanel>

      {/* View-segment lightweight popover */}
      <OverlayPanel
        ref={viewOverlayRef}
        className="context-switcher-toolbar__view-popover"
        onShow={() => setIsViewPopoverVisible(true)}
        onHide={() => setIsViewPopoverVisible(false)}
        dismissable
        appendTo={document.body}
      >
        {isViewPopoverVisible && (
          <ViewSwitcherContent
            activeContextSystemName={activeContextSystemName}
            activeContextDisplayName={activeContextDisplayName}
            viewType={viewType}
            activeViewSystemName={activeViewSystemName}
            switcherRefreshKey={switcherRefreshKey}
            onPickDefault={() => handleContextSelect(activeContextSystemName, activeContextDisplayName)}
            onPickView={(viewSn, viewName, ctxSn, ctxName) =>
              handleViewSelect(viewSn, viewName, ctxSn, ctxName)
            }
            onNewView={onNewView ? () => handleNewView(activeContextSystemName, activeContextDisplayName) : undefined}
            onSwitchContext={handleSwitchContextFromView}
            onEditView={onEditView ? (viewSn, ctxSn) => {
              viewOverlayRef.current?.hide();
              onEditView(viewSn, ctxSn);
            } : undefined}
            onClose={() => viewOverlayRef.current?.hide()}
          />
        )}
      </OverlayPanel>

      {/* Narrow viewport overflow panel */}
      <OverlayPanel
        ref={overflowRef}
        className="context-switcher-toolbar__overflow-panel"
        dismissable
        appendTo={document.body}
      >
        <div className="context-switcher-toolbar__overflow-actions">
          <button
            type="button"
            className="context-switcher-toolbar__overflow-action"
            onClick={handleSave}
            disabled={!canEditActiveContext || !onSave || isSaving}
          >
            {t('contextSwitcher.save')}
          </button>
          <button
            type="button"
            className="context-switcher-toolbar__overflow-action"
            onClick={handleSaveAsCopyOpen}
            disabled={!onSaveAsCopy || isSaving}
          >
            {t('contextSwitcher.saveAsCopy')}
          </button>
        </div>
      </OverlayPanel>

      {/* Save-as-copy dialog */}
      <CustomDialogAtom
        header={t('contextSwitcher.saveAsCopyTitle')}
        visible={isSaveAsCopyOpen}
        onHide={() => !isSaving && setIsSaveAsCopyOpen(false)}
        footer={saveAsCopyFooter}
        style={{ width: '480px', maxWidth: '90vw', minHeight: 'unset' }}
        className="context-switcher-toolbar__save-dialog"
      >
        <div className="context-switcher-toolbar__save-form">
          <label className="context-switcher-toolbar__save-label" htmlFor="ctx-copy-name">
            {t('contextSwitcher.saveName')}
            <span aria-hidden="true"> *</span>
          </label>
          <input
            id="ctx-copy-name"
            type="text"
            className="context-switcher-toolbar__save-input"
            value={saveAsCopyName}
            onChange={(e) => setSaveAsCopyName(e.target.value)}
          />
          <label className="context-switcher-toolbar__save-label" htmlFor="ctx-copy-desc">
            {t('contextSwitcher.saveDescription')}
          </label>
          <textarea
            id="ctx-copy-desc"
            className="context-switcher-toolbar__save-textarea"
            value={saveAsCopyDescription}
            onChange={(e) => setSaveAsCopyDescription(e.target.value)}
            rows={3}
          />
          <p className="context-switcher-toolbar__save-note">{t('contextSwitcher.saveNote')}</p>
        </div>
      </CustomDialogAtom>

      {/* Discard-changes confirmation dialog */}
      <CustomDialogAtom
        header={t('contextSwitcher.discardTitle')}
        visible={isDiscardOpen}
        onHide={handleDiscardCancel}
        footer={discardFooter}
        style={{ width: '480px', maxWidth: '90vw', minHeight: 'unset' }}
        className="context-switcher-toolbar__discard-dialog"
      >
        <p className="context-switcher-toolbar__discard-message">{discardMessage}</p>
      </CustomDialogAtom>
    </>
  );
});

ContextSwitcherToolbarMolecule.displayName = 'ContextSwitcherToolbarMolecule';

export default ContextSwitcherToolbarMolecule;
